import React, { useState, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import { Edit, ArrowUp, ArrowDown, Plus, Trash2, Save, Upload, X } from "lucide-react";
import adminApi from "../adminApi";
import { backendUrl } from "../../backendUrl";
import "./LockProperties.css"; // Use same CSS as LockProperties

interface Supplier {
  id: number;
  name: string;
  icon_path: string | null;
  display_order: number;
  is_active: number | boolean;
  created_at?: string;
}

interface IconFile {
  name: string;
  path: string;
}

function SupplierIcon() {
  const { t } = useLanguage();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [availableIcons, setAvailableIcons] = useState<IconFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get("/suppliers");
      setSuppliers(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching suppliers:", err);
      setError(t("Failed to load suppliers"));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableIcons = async () => {
    try {
      const response = await adminApi.get("/supplier-icons/icons");
      setAvailableIcons(response.data);
    } catch (err) {
      console.error("Error fetching available icons:", err);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchAvailableIcons();
  }, []);

  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('icon', file);

    try {
      setUploadingIcon(true);
      const response = await adminApi.post(
        "/supplier-icons/icons",
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      await fetchAvailableIcons();
      setEditIcon(response.data.path);
      alert(t("Icon uploaded successfully!"));
    } catch (error) {
      console.error('Error uploading icon:', error);
      alert(t("Failed to upload icon"));
    } finally {
      setUploadingIcon(false);
      event.target.value = '';
    }
  };

  const handleIconDelete = async (iconName: string) => {
    if (!window.confirm(`${t("Are you sure you want to delete the icon")} "${iconName}"?`)) {
      return;
    }

    try {
      await adminApi.delete(`/supplier-icons/icons/${iconName}`);
      await fetchAvailableIcons();

      if (editIcon === `/img/supplier/${iconName}`) {
        setEditIcon("");
      }

      alert(`${t("Icon")} "${iconName}" ${t("deleted successfully")}`);
    } catch (err: any) {
      console.error("Error deleting icon:", err);
      alert(t("Failed to delete icon"));
    }
  };

  const handleToggleActive = async (id: number) => {
    const supplier = suppliers.find(s => s.id === id);
    if (!supplier) return;

    const newActiveState = !supplier.is_active;

    const updatedSuppliers = suppliers.map(s =>
      s.id === id ? { ...s, is_active: newActiveState } : s
    );

    setSuppliers(updatedSuppliers);

    try {
      await adminApi.put(`/suppliers/${id}`, {
        id,
        name: supplier.name,
        icon_path: supplier.icon_path,
        display_order: supplier.display_order,
        is_active: newActiveState
      });
    } catch (error) {
      console.error("Error toggling supplier:", error);
      setSuppliers(suppliers);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newSuppliers = [...suppliers];
    [newSuppliers[index - 1], newSuppliers[index]] = [newSuppliers[index], newSuppliers[index - 1]];

    // Update display_order for swapped items
    newSuppliers[index - 1].display_order = index - 1;
    newSuppliers[index].display_order = index;

    setSuppliers(newSuppliers);

    try {
      // Save only the two swapped suppliers
      await Promise.all([
        adminApi.put(`/suppliers/${newSuppliers[index - 1].id}`, {
          display_order: newSuppliers[index - 1].display_order
        }),
        adminApi.put(`/suppliers/${newSuppliers[index].id}`, {
          display_order: newSuppliers[index].display_order
        })
      ]);
    } catch (error) {
      console.error("Error moving supplier:", error);
      setSuppliers(suppliers);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === suppliers.length - 1) return;
    const newSuppliers = [...suppliers];
    [newSuppliers[index], newSuppliers[index + 1]] = [newSuppliers[index + 1], newSuppliers[index]];

    // Update display_order for swapped items
    newSuppliers[index].display_order = index;
    newSuppliers[index + 1].display_order = index + 1;

    setSuppliers(newSuppliers);

    try {
      // Save only the two swapped suppliers
      await Promise.all([
        adminApi.put(`/suppliers/${newSuppliers[index].id}`, {
          display_order: newSuppliers[index].display_order
        }),
        adminApi.put(`/suppliers/${newSuppliers[index + 1].id}`, {
          display_order: newSuppliers[index + 1].display_order
        })
      ]);
    } catch (error) {
      console.error("Error moving supplier:", error);
      setSuppliers(suppliers);
    }
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingId(supplier.id);
    setEditName(supplier.name);
    setEditIcon(supplier.icon_path || "");
  };

  const handleSaveEdit = async () => {
    if (editingId === null) return;

    try {
      await adminApi.put(`/suppliers/${editingId}`, {
        id: editingId,
        name: editName,
        icon_path: editIcon,
        display_order: suppliers.find(s => s.id === editingId)?.display_order || 0,
        is_active: suppliers.find(s => s.id === editingId)?.is_active || false
      });

      await fetchSuppliers();
      setEditingId(null);
      setEditName("");
      setEditIcon("");
    } catch (error) {
      console.error("Error saving supplier:", error);
      alert(t("Failed to save supplier"));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditIcon("");
  };

  const handleAdd = async () => {
    try {
      const response = await adminApi.post("/suppliers", {
        name: t("New Supplier"),
        icon_path: ""
      });

      if (response.data.success) {
        await fetchSuppliers();
      }
    } catch (err) {
      console.error("Error adding supplier:", err);
      setError(t("Failed to add supplier"));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t("Are you sure you want to delete this supplier?"))) return;

    try {
      await adminApi.delete(`/suppliers/${id}`);
      await fetchSuppliers();
    } catch (err) {
      console.error("Error deleting supplier:", err);
      setError(t("Failed to delete supplier"));
    }
  };

  if (loading) return <div className="loading">{t("Loading...")}</div>;
  if (error) return <div className="error">{error}</div>;

  const activeCount = suppliers.filter(s => s.is_active).length;

  return (
    <div className="lock-properties">
      <div className="properties-header">
        <div>
          <h2>{t("Supplier Management")}</h2>
          <p className="properties-subtitle">
            {t("Active suppliers:")} {activeCount} {t("(displayed on pages 2, 3, and 4)")}
          </p>
        </div>
        <button className="btn-add" onClick={handleAdd}>
          <Plus size={16} /> {t("Add Supplier")}
        </button>
      </div>

      {suppliers.length === 0 ? (
        <div className="empty-state">
          <p>{t("No suppliers found. Click \"Add Supplier\" to create one.")}</p>
        </div>
      ) : (
        <ul>
          {suppliers.map((supplier, index) => (
            <li
              key={supplier.id}
              className={`property-item ${supplier.is_active ? 'active' : 'inactive'}`}
            >
              {editingId === supplier.id ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>{t("Name:")}</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder={t("Supplier name")}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t("Icon:")}</label>
                    <div className="icon-selector">
                      <select
                        value={editIcon}
                        onChange={(e) => setEditIcon(e.target.value)}
                        className="icon-dropdown"
                      >
                        <option value="">{t("-- Select Icon --")}</option>
                        {availableIcons.map((icon) => (
                          <option key={icon.path} value={icon.path}>
                            {icon.name}
                          </option>
                        ))}
                      </select>

                      <label className="upload-btn">
                        <Upload size={16} />
                        {uploadingIcon ? t("Uploading...") : t("Upload New")}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleIconUpload}
                          style={{ display: 'none' }}
                          disabled={uploadingIcon}
                        />
                      </label>
                    </div>

                    {/* Icon Management Section */}
                    {availableIcons.length > 0 && (
                      <div className="icon-management">
                        <h4>{t("Available Icons:")}</h4>
                        <div className="icon-grid">
                          {availableIcons.map((icon) => (
                            <div key={icon.path} className="icon-item-small">
                              <img
                                src={`${backendUrl}${icon.path}`}
                                alt={icon.name}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              <span className="icon-name">{icon.name}</span>
                              <button
                                className="delete-icon-btn"
                                onClick={() => handleIconDelete(icon.name)}
                                title={`${t("Delete")} ${icon.name}`}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {editIcon && (
                      <div className="icon-preview">
                        <img
                          src={`${backendUrl}${editIcon}`}
                          alt={t("Icon preview")}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button className="btn-save" onClick={handleSaveEdit}>
                      <Save size={16} /> {t("Save")}
                    </button>
                    <button className="btn-cancel" onClick={handleCancelEdit}>
                      <X size={16} /> {t("Cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="property-info">
                    <div className="property-details">
                      <span className="property-name">{supplier.name}</span>
                      {supplier.icon_path && (
                        <div className="property-icon-display">
                          <img
                            src={`${backendUrl}${supplier.icon_path}`}
                            alt={supplier.name}
                            style={{ width: '28px', height: '28px' }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          <span className="property-icon-path">{supplier.icon_path}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="property-actions">
                    <button className="edit-btn" onClick={() => handleEdit(supplier)}>
                      <Edit size={16} />
                    </button>
                    <button
                      className="arrow-btn"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      className="arrow-btn"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === suppliers.length - 1}
                    >
                      <ArrowDown size={16} />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(supplier.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SupplierIcon;
